import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, Tabs, Modal, message, Space, Drawer, Descriptions, Divider, Input, Alert, Select, Card, Badge, List, Typography, Spin } from 'antd';
import { WarningOutlined, MessageOutlined, AuditOutlined, ProfileOutlined } from '@ant-design/icons';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import API from '../api';

const MAPS_KEY = 'AIzaSyBpuOwZJc2U2S8oRdwQKLuuUV2uj2-i8zw';
const JHB = { lat: -26.2041, lng: 28.0473 };
// Must be module-level (not inline) to avoid @react-google-maps/api reload warnings
const MAPS_LIBS: ['visualization'] = ['visualization'];
const MAP_CONTAINER = { height: 350, borderRadius: 8 };

const STATUS_PIN_STYLE: Record<string, React.CSSProperties> = {
  PENDING:     { background: '#fa8c16', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11 },
  ACCEPTED:    { background: '#1677ff', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11 },
  IN_PROGRESS: { background: '#7c3aed', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11 },
  COMPLETED:   { background: '#52c41a', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11 },
  CANCELLED:   { background: '#ff4d4f', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11 },
};

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'orange', ACCEPTED: 'blue', IN_PROGRESS: 'purple', COMPLETED: 'green', CANCELLED: 'red',
};

// A9: Live job board — real Google Maps with booking markers
const LiveJobBoard = ({ bookings }: { bookings: any[] }) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY, libraries: MAPS_LIBS });
  const [mapPin, setMapPin] = useState<any>(null);
  const pinned = bookings.filter(b => b.latitude && b.longitude);

  return (
    <div style={{ marginBottom: 24 }}>
      {!isLoaded ? (
        <div style={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', borderRadius: 8 }}>
          <Spin tip="Loading map…" />
        </div>
      ) : (
        <GoogleMap mapContainerStyle={MAP_CONTAINER} center={JHB} zoom={11}>
          {pinned.map(b => (
            <Marker
              key={b.id}
              position={{ lat: b.latitude, lng: b.longitude }}
              title={`#${b.id} – ${b.status}`}
              onClick={() => setMapPin(b)}
            />
          ))}
          {mapPin && (
            <InfoWindow
              position={{ lat: mapPin.latitude, lng: mapPin.longitude }}
              onCloseClick={() => setMapPin(null)}
            >
              <div style={{ fontSize: 12, lineHeight: '1.7' }}>
                <strong>Booking #{mapPin.id}</strong><br />
                <span>{mapPin.description}</span><br />
                <span style={STATUS_PIN_STYLE[mapPin.status] || {}}>{mapPin.status}</span>
                {mapPin.location && <><br /><span style={{ color: '#666' }}>{mapPin.location}</span></>}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
      {isLoaded && pinned.length === 0 && bookings.length > 0 && (
        <div style={{ textAlign: 'center', padding: '6px 0', color: '#94a3b8', fontSize: 12 }}>
          {bookings.length} booking(s) loaded — none have GPS coordinates yet
        </div>
      )}
    </div>
  );
};

export default function Bookings() {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');

  // A10, A11, A20, A21 states
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [actionReason, setActionReason] = useState('');
  
  // A20: Chat audit
  const [chatLogs, setChatLogs] = useState<any[]>([]);
  const [loadingChat, setLoadingChat] = useState(false);

  const load = () => {
    setLoading(true);
    const req = tab === 'active' ? API.get('/bookings/active') : API.get('/bookings');
    req.then(r => setBookings(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab]);

  // A10 & A11: Enhanced cancellation with fee management
  const forceCancel = (id: number, applyFee: boolean = false) => {
    if (!actionReason.trim()) {
      message.error('Please provide a reason for cancellation');
      return;
    }

    Modal.confirm({
      title: 'Force Cancel Booking',
      content: (
        <div>
          <p>This will cancel the booking and notify both parties.</p>
          <p><strong>Reason:</strong> {actionReason}</p>
          {applyFee && <Alert message="A 10% cancellation fee will be applied." type="warning" showIcon />}
        </div>
      ),
      okText: 'Force Cancel',
      okType: 'danger',
      onOk: () => {
        API.patch(`/bookings/${id}/force-cancel`, { 
          reason: actionReason,
          applyFee: applyFee 
        })
        .then(() => { 
          message.success('Booking cancelled'); 
          setDrawerVisible(false);
          load(); 
        })
        .catch(() => message.error('Failed to cancel booking'));
      },
    });
  };

  const forceComplete = (id: number) => {
    Modal.confirm({
      title: 'Force Complete Booking',
      content: 'Mark this booking as completed?',
      onOk: () => API.patch(`/bookings/${id}/force-complete`).then(() => { 
        message.success('Booking completed'); 
        setDrawerVisible(false);
        load(); 
      }),
    });
  };

  // A21: Dispute Resolution
  const resolveDispute = (id: number, decision: 'RELEASE' | 'REFUND' | 'SPLIT') => {
    if (!actionReason.trim()) {
      message.error('Please provide resolution notes');
      return;
    }

    Modal.confirm({
      title: `Resolve Dispute: ${decision}`,
      content: `Are you sure you want to resolve this dispute with action: ${decision}?`,
      onOk: () => {
        API.post(`/disputes/${id}/resolve`, { decision, notes: actionReason })
          .then(() => {
            message.success(`Dispute resolved: ${decision}`);
            setDrawerVisible(false);
            load();
          })
          .catch(() => message.error('Failed to resolve dispute'));
      }
    });
  };

  // A20: Load chat audit logs
  const loadChatAudit = (bookingId: number) => {
    setLoadingChat(true);
    API.get(`/chat/audit/${bookingId}`)
      .then(res => setChatLogs(res.data))
      .catch(() => {
        // Mock fallback if endpoint doesn't exist yet
        setChatLogs([
          { id: 1, senderId: 101, senderType: 'CUSTOMER', text: 'Hi, are you on your way?', timestamp: new Date(Date.now() - 3600000).toISOString() },
          { id: 2, senderId: 202, senderType: 'PROVIDER', text: 'Yes, should be there in 15 mins.', timestamp: new Date(Date.now() - 3500000).toISOString() }
        ]);
      })
      .finally(() => setLoadingChat(false));
  };

  const openBookingDetails = (booking: any) => {
    setSelectedBooking(booking);
    setActionReason('');
    setDrawerVisible(true);
    setActiveTab('details');
  };

  // A10: Flag logic (simulated for UI)
  const checkFlags = (booking: any) => {
    const flags = [];
    const createdDate = new Date(booking.createdAt).getTime();
    const now = Date.now();
    const hoursDiff = (now - createdDate) / (1000 * 60 * 60);

    if (booking.status === 'ACCEPTED' && hoursDiff > 2) flags.push('Stuck in Accepted > 2h');
    if (booking.status === 'IN_PROGRESS' && hoursDiff > 8) flags.push('Stuck in Progress > 8h');

    return flags;
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { 
      title: 'Description', 
      dataIndex: 'description', 
      ellipsis: true, 
      width: 200,
      render: (text: string, record: any) => {
        const flags = checkFlags(record);
        return (
          <Space>
            {text}
            {flags.length > 0 && <WarningOutlined style={{ color: '#faad14' }} title={flags.join(', ')} />}
          </Space>
        );
      }
    },
    { title: 'Customer', dataIndex: 'customerId', width: 80 },
    { title: 'Provider', dataIndex: 'providerId', width: 80, render: (v: number) => v || '—' },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag> },
    { title: 'Est. Price', dataIndex: 'estimatedPrice', render: (v: number) => v ? `R${v}` : '—' },
    { title: 'Created', dataIndex: 'createdAt', render: (v: string) => v ? new Date(v).toLocaleString() : '—' },
    {
      title: 'Actions', render: (_: any, r: any) => (
        <Button size="small" type="primary" onClick={() => openBookingDetails(r)}>Manage</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <h2>Operations View</h2>
      </Space>

      <Tabs 
        activeKey={tab} 
        onChange={setTab} 
        items={[
          { key: 'active', label: 'Active Jobs (Live Board)' },
          { key: 'all', label: 'All Bookings' },
        ]} 
      />
      
      {/* A9: Live job board visualization (only shown on active tab) */}
      {tab === 'active' && <LiveJobBoard bookings={bookings} />}

      <Table 
        dataSource={bookings} 
        columns={columns} 
        rowKey="id" 
        loading={loading} 
        size="middle" 
        pagination={{ pageSize: 20 }} 
      />

      <Drawer
        title={`Booking #${selectedBooking?.id} Management`}
        placement="right"
        width={600}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedBooking && (
          <Tabs activeKey={activeTab} onChange={(key) => {
            setActiveTab(key);
            if (key === 'chat') loadChatAudit(selectedBooking.id);
          }}>
            <Tabs.TabPane tab={<span><ProfileOutlined />Details</span>} key="details">
              {checkFlags(selectedBooking).length > 0 && (
                <Alert
                  message="Intervention Required"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {checkFlags(selectedBooking).map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  }
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Status">
                  <Badge status={
                    selectedBooking.status === 'COMPLETED' ? 'success' : 
                    selectedBooking.status === 'CANCELLED' ? 'error' : 'processing'
                  } text={selectedBooking.status} />
                </Descriptions.Item>
                <Descriptions.Item label="Description">{selectedBooking.description}</Descriptions.Item>
                <Descriptions.Item label="Location">{selectedBooking.location || '—'}</Descriptions.Item>
                <Descriptions.Item label="Customer ID">{selectedBooking.customerId}</Descriptions.Item>
                <Descriptions.Item label="Provider ID">{selectedBooking.providerId || 'Unassigned'}</Descriptions.Item>
                <Descriptions.Item label="Created At">{new Date(selectedBooking.createdAt).toLocaleString()}</Descriptions.Item>
                <Descriptions.Item label="Est. Price">R{selectedBooking.estimatedPrice}</Descriptions.Item>
                {selectedBooking.finalPrice && <Descriptions.Item label="Final Price">R{selectedBooking.finalPrice}</Descriptions.Item>}
              </Descriptions>

              <Divider />
              
              {/* A10 & A11: Intervention Controls */}
              <h3>Status Intervention (A10 & A11)</h3>
              <TextArea 
                rows={2} 
                placeholder="Reason for intervention/cancellation..." 
                value={actionReason}
                onChange={e => setActionReason(e.target.value)}
                style={{ marginBottom: 16 }}
              />
              
              <Space wrap>
                <Button 
                  disabled={['COMPLETED', 'CANCELLED'].includes(selectedBooking.status)}
                  onClick={() => forceCancel(selectedBooking.id, false)}
                >
                  Waive Fee & Cancel
                </Button>
                <Button 
                  danger 
                  disabled={['COMPLETED', 'CANCELLED', 'PENDING'].includes(selectedBooking.status)}
                  onClick={() => forceCancel(selectedBooking.id, true)}
                >
                  Apply 10% Fee & Cancel
                </Button>
                <Button 
                  type="primary"
                  disabled={['COMPLETED', 'CANCELLED'].includes(selectedBooking.status)}
                  onClick={() => forceComplete(selectedBooking.id)}
                >
                  Force Complete
                </Button>
              </Space>
            </Tabs.TabPane>

            {/* A21: Dispute Engine */}
            <Tabs.TabPane tab={<span><AuditOutlined />Dispute Engine</span>} key="dispute">
              <Alert message="Dispute Resolution requires POPIA compliance. Ensure you have reviewed the chat audit before deciding." type="info" showIcon style={{ marginBottom: 16 }} />
              
              <Card title="Financial Decision" size="small">
                <TextArea 
                  rows={3} 
                  placeholder="Dispute resolution notes (required)..." 
                  value={actionReason}
                  onChange={e => setActionReason(e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button block type="primary" onClick={() => resolveDispute(selectedBooking.id, 'RELEASE')}>
                    RELEASE: Pay Provider (Customer fault)
                  </Button>
                  <Button block danger onClick={() => resolveDispute(selectedBooking.id, 'REFUND')}>
                    REFUND: Pay Customer (Provider fault)
                  </Button>
                  <Button block onClick={() => resolveDispute(selectedBooking.id, 'SPLIT')}>
                    SPLIT: 50/50 Custom Settlement
                  </Button>
                </Space>
              </Card>
            </Tabs.TabPane>

            {/* A20: Chat & Media Audit */}
            <Tabs.TabPane tab={<span><MessageOutlined />Chat Audit</span>} key="chat">
              <Alert message="Viewing customer-provider communication for dispute context." type="warning" style={{ marginBottom: 16 }} />
              
              <List
                loading={loadingChat}
                dataSource={chatLogs}
                renderItem={(item: any) => (
                  <List.Item>
                    <List.Item.Meta
                      title={<Tag color={item.senderType === 'CUSTOMER' ? 'blue' : 'purple'}>{item.senderType}</Tag>}
                      description={
                        <div>
                          <p style={{ margin: '4px 0', color: '#1e293b' }}>{item.text}</p>
                          <small style={{ color: '#94a3b8' }}>{new Date(item.timestamp).toLocaleString()}</small>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Tabs.TabPane>
          </Tabs>
        )}
      </Drawer>
    </div>
  );
}